/**
 * Sentry Wrapper Component
 *
 * Conditionally initializes Sentry and wraps children with ErrorBoundary
 * Only activates when running in iframe/SDK context
 */

import * as Sentry from "@sentry/react";
import { ReactNode, useEffect } from "react";
import {
  SENTRY_CONFIG,
  VERIFICATION_PATHS,
  URL_PARAMS,
  FLOW_TYPES,
  type VerificationFlowType
} from "../shared/constants";
import { getEnvironmentFromHostname } from "../shared/utils/apiConfig";

interface SentryWrapperProps {
  children: ReactNode;
}

/**
 * Synchronously check if we're in iframe/SDK context
 * This runs during module initialization so ErrorBoundary wraps from first render
 */
function checkIsIframeContext(): boolean {
  if (typeof window === "undefined") return false;

  const urlParams = new URLSearchParams(window.location.search ?? "");
  const pathname = window.location.pathname ?? "";
  return (
    urlParams.has(URL_PARAMS.SESSION_TOKEN) ||
    pathname.includes(VERIFICATION_PATHS.FACE_VERIFICATION) ||
    pathname.includes(VERIFICATION_PATHS.LEGAL_ID_VERIFICATION) ||
    pathname.includes(VERIFICATION_PATHS.REGULA_LEGAL_ID_VERIFICATION) ||
    pathname.includes(VERIFICATION_PATHS.SSN_CARD_VERIFICATION)
  );
}

/**
 * Determine verification flow type from URL
 */
function getFlowType(): VerificationFlowType {
  if (typeof window === "undefined") return FLOW_TYPES.UNKNOWN;

  const pathname = window.location.pathname ?? "";
  if (pathname.includes(VERIFICATION_PATHS.FACE_VERIFICATION)) {
    return FLOW_TYPES.FACE_VERIFICATION;
  }
  if (
    pathname.includes(VERIFICATION_PATHS.LEGAL_ID_VERIFICATION) ||
    pathname.includes(VERIFICATION_PATHS.REGULA_LEGAL_ID_VERIFICATION)
  ) {
    return FLOW_TYPES.LEGAL_ID;
  }
  if (pathname.includes(VERIFICATION_PATHS.SSN_CARD_VERIFICATION)) {
    return FLOW_TYPES.SSN_CARD;
  }
  return FLOW_TYPES.UNKNOWN;
}

/**
 * Initialize Sentry if not already initialized
 * Uses Sentry.getClient() to check initialization state (survives HMR)
 */
function initializeSentry(): void {
  // Check if Sentry is already initialized (handles HMR and StrictMode)
  if (Sentry.getClient()) {
    if (import.meta.env.DEV) {
      console.log("[WebSDK] Sentry already initialized, skipping");
    }
    return;
  }

  const flowType = getFlowType();

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: SENTRY_CONFIG.TRACES_SAMPLE_RATE,
    replaysSessionSampleRate: SENTRY_CONFIG.REPLAYS_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: SENTRY_CONFIG.REPLAYS_ON_ERROR_SAMPLE_RATE,
    environment: getEnvironmentFromHostname(),
    initialScope: {
      tags: {
        context: SENTRY_CONFIG.CONTEXT_TAG,
        flow: flowType,
      },
    },
  });

  if (import.meta.env.DEV) {
    console.log(`[WebSDK] Sentry initialized for iframe context (${flowType} flow)`);
  }
}

// Detect iframe context synchronously at module load
const isIframeContext = checkIsIframeContext();

// Initialize Sentry immediately if in iframe context
if (isIframeContext) {
  initializeSentry();
}

/**
 * Error fallback - notify parent window and log, no UI needed
 * Parent app handles user-facing error display via onError callback
 */
function ErrorFallback({ error }: { error: unknown }) {
  const errorMessage = error instanceof Error ? error.message : "Unknown error";

  useEffect(() => {
    console.error("[WebSDK] React error boundary caught error:", error);

    // Notify parent window so SDK consumer can handle cleanup
    window.parent.postMessage(
      {
        source: "idme-verification",
        type: "VERIFICATION_ERROR",
        payload: {
          errorCode: "internal_error",
          message: "An unexpected error occurred",
          data: {
            uuid: "unknown",
            type: "FACE_VERIFICATION",
            status: "failed",
            outcome: "failed",
            error: errorMessage,
          },
        },
      },
      "*"
    );
  }, [error, errorMessage]);

  // Return empty - parent window will clean up iframe after receiving error
  return null;
}

/**
 * Handle error boundary errors - log to Sentry and notify parent
 */
function handleErrorBoundaryError(error: unknown, componentStack: string, _eventId: string): void {
  Sentry.withScope((scope) => {
    scope.setTag("error_boundary", "true");
    scope.setContext("react", { componentStack });
    Sentry.captureException(error);
  });
}

/**
 * SentryWrapper Component
 *
 * Initializes Sentry when in iframe context and wraps children with ErrorBoundary.
 * Detection happens synchronously so ErrorBoundary is present from first render.
 */
export function SentryWrapper({ children }: SentryWrapperProps) {
  if (isIframeContext) {
    return (
      <Sentry.ErrorBoundary
        fallback={({ error }) => <ErrorFallback error={error} />}
        onError={handleErrorBoundaryError}
      >
        {children}
      </Sentry.ErrorBoundary>
    );
  }

  return <>{children}</>;
}
